import { ModelOption } from "../types/shared.types"; // Assuming ModelOption type exists or needs creation

/**
 * Returns a user-friendly display name for a model key.
 */
export const getModelDisplayName = (modelKey: string): string => {
  switch (modelKey) {
    case "xlm_roberta":
    case "xlm-roberta-large-finetuned-conll03-english": // From RedactionTool
      return "XLM-RoBERTa";
    case "bert":
    case "dbmdz/bert-large-cased-finetuned-conll03-english": // From RedactionTool
      return "BERT";
    case "ensemble-xlm-bert":
      return "DUAL SYSTEM (BERT + XLM-RoBERTa)";
    default:
      return modelKey; // Fallback to the key itself
  }
};

/**
 * Provides an explanation for an NER entity type.
 */
export const getEntityTypeExplanation = (entityType: string): string => {
  switch (entityType) {
    case "PER":
      return "Person - A human individual";
    case "ORG":
      return "Organization - Companies, agencies, institutions";
    case "LOC":
      return "Location - Physical locations, countries, cities";
    case "MISC":
      return "Miscellaneous - Other named entities";
    default:
      return entityType; // Fallback
  }
};

/**
 * Returns a CSS class string based on a confidence score.
 */
export const getConfidenceClass = (score: number): string => {
  if (score > 0.9) return "text-success fw-bold"; // High confidence
  if (score > 0.7) return "text-warning"; // Medium confidence
  return "text-danger"; // Low confidence
};

/**
 * Returns a Bootstrap background class string based on an entity type.
 */
export const getEntityTypeBgClass = (entityType: string): string => {
  switch (entityType) {
    case "PER":
      return "success"; // Green for Person
    case "ORG":
      return "primary"; // Blue for Organization
    case "LOC":
      return "info"; // Cyan for Location
    case "MISC":
      return "warning"; // Yellow for Miscellaneous
    default:
      return "secondary"; // Grey fallback
  }
};

/**
 * Formats a number as a percentage string.
 */
export const formatPercentage = (value: number | undefined): string => {
  return value !== undefined ? `${Number(value).toFixed(1)}%` : "N/A";
};

/**
 * Default models used in most analysis components.
 */
export const defaultAnalysisModels: ModelOption[] = [
  { value: "xlm_roberta", label: "XLM-RoBERTa" },
  { value: "bert", label: "BERT" },
];

/**
 * Models used specifically in the Redaction tool.
 * Removed GLiNER Medium and Dual System for better performance.
 */
export const redactionModels: ModelOption[] = [
  {
    label: "XLM-RoBERTa (Large)",
    value: "xlm-roberta-large-finetuned-conll03-english",
  },
  {
    label: "BERT (Large Cased)",
    value: "dbmdz/bert-large-cased-finetuned-conll03-english",
  },
  {
    label: "GLiNER (Base)",
    value: "urchade/gliner_base",
  },
  {
    label: "GLiNER (Small v2.5 Community)",
    value: "gliner-community/gliner_small-v2.5",
  },
];

// Color scheme for better visual appeal in stats and charts
export const STATS_COLORS = {
  primary: "#0088FE",
  secondary: "#00C49F",
  accent: "#FFBB28",
  warning: "#FF8042",
  success: "#82ca9d",
  info: "#8884d8",
  purple: "#9c27b0",
  orange: "#ff9800",
};
